/**
 * Creates a rem unit function that compensates for legacy (non-Remix) pages
 * where the root font-size is 1px instead of the standard 16px.
 *
 * In legacy mode, values are multiplied by 16 so that e.g. `rem(1)` produces
 * `"16rem"` (visually equivalent to the normal `"1rem"` at 16px root).
 * In non-legacy mode, values pass through unchanged.
 *
 * @param isLegacyBrokenRemMode - Whether the page has a 1px root font-size.
 * @returns A function that converts a design-intent rem value to a CSS string.
 */
export const createRemUnit =
    (isLegacyBrokenRemMode?: boolean) =>
    (value: number): string =>
        `${isLegacyBrokenRemMode ? value * 16 : value}rem`;

/**
 * Creates a style helper that only applies inline styles in legacy mode.
 *
 * In legacy mode the returned function passes styles through (so they can
 * override Tailwind classes with rem-corrected values). In non-legacy mode
 * it returns `undefined`, letting Tailwind classes be the sole source of truth.
 *
 * @param isLegacyBrokenRemMode - Whether the page has a 1px root font-size.
 * @returns A function that conditionally returns the styles or `undefined`.
 */
export const createLegacyStyle =
    (isLegacyBrokenRemMode?: boolean) =>
    <T>(styles: T): T | undefined =>
        isLegacyBrokenRemMode ? styles : undefined;
